import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Building2, RefreshCw, Plus, ChevronRight, Sofa, Bed, UtensilsCrossed, Archive, Briefcase, Store, Hotel, Move, Palette, Boxes, Layout as LayoutIcon, Box } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { services, portfolioImages } from '../data/mock';

const ServicesPage = () => {
  const serviceCategories = [
    {
      id: 'residential',
      icon: Home,
      title: 'Residential Interiors',
      subtitle: 'Spaces that feel like home',
      description: 'Transform your house into a sanctuary that reflects your personality and enhances your daily life.',
      image: portfolioImages.livingRooms[0],
      items: [
        { icon: Sofa, title: 'Sophisticated Living Rooms', desc: 'Elegant spaces that balance comfort and style, designed for modern living and entertaining.' },
        { icon: Bed, title: 'Personalized Bedrooms', desc: 'Cozy, personalized sanctuaries that reflect your unique personality and promote restful sleep.' },
        { icon: UtensilsCrossed, title: 'Elegant Kitchens & Dining', desc: 'Functional, beautiful spaces where culinary dreams come to life and families gather.' },
        { icon: Archive, title: 'Seamless Storage Solutions', desc: 'Smart storage that keeps your space organized without compromising style or aesthetics.' }
      ]
    },
    {
      id: 'commercial',
      icon: Building2,
      title: 'Commercial Interiors',
      subtitle: 'Spaces that inspire success',
      description: 'Create professional environments that boost productivity, impress clients, and tell your brand story.',
      image: portfolioImages.offices[0],
      items: [
        { icon: Briefcase, title: 'Productivity-Driven Offices', desc: 'Workspaces designed to inspire creativity, enhance collaboration, and boost productivity.' },
        { icon: Store, title: 'Engaging Retail Spaces', desc: 'Captivating environments that tell your brand story and create memorable shopping experiences.' },
        { icon: Hotel, title: 'Elegant Hospitality Interiors', desc: 'Warm, welcoming spaces that leave lasting impressions on guests and patrons.' }
      ]
    },
    {
      id: 'landscape',
      icon: RefreshCw,
      title: 'Landscape',
      subtitle: 'Transform your outdoor spaces',
      description: 'Create stunning outdoor environments that extend your living space and connect you with nature.',
      image: portfolioImages.kitchens[1],
      items: [
        { icon: Move, title: 'Garden Design', desc: 'Transform your outdoor space into a beautiful, functional garden with curated plantings.' },
        { icon: Palette, title: 'Outdoor Living Areas', desc: 'Create stunning patios, decks, and outdoor entertainment spaces for year-round enjoyment.' },
        { icon: RefreshCw, title: 'Sustainable Landscaping', desc: 'Eco-friendly landscape solutions that conserve water and enhance natural beauty.' }
      ]
    },
    {
      id: 'villas-resorts',
      icon: Hotel,
      title: 'Villas & Resorts',
      subtitle: 'Luxury hospitality interiors',
      description: 'Premium interior design for exclusive villas, resorts, and hospitality spaces that create memorable experiences.',
      image: portfolioImages.bedrooms[2],
      items: [
        { icon: Briefcase, title: 'Luxury Villa Interiors', desc: 'Premium interior design for exclusive villas and private residences with bespoke finishes.' },
        { icon: Hotel, title: 'Resort & Hospitality', desc: 'Creating memorable guest experiences through thoughtful design and attention to detail.' },
        { icon: Box, title: 'Pool & Spa Areas', desc: 'Relaxing wellness spaces that blend seamlessly with natural surroundings.' }
      ]
    },
    {
      id: 'additional',
      icon: Plus,
      title: 'Additional Services',
      subtitle: 'Complete design solutions',
      description: 'From modular solutions to visualization, we offer comprehensive services to bring your vision to life.',
      image: portfolioImages.bedrooms[1],
      items: [
        { icon: Boxes, title: 'Modular Kitchens', desc: 'Custom-designed modular kitchens tailored to your cooking style and storage needs.' },
        { icon: Box, title: 'Custom Wardrobes', desc: 'Bespoke wardrobe solutions that fit your lifestyle and maximize your storage space.' },
        { icon: LayoutIcon, title: 'Conceptual Designs', desc: 'Creative visions brought to life through detailed concept development and planning.' },
        { icon: Box, title: '3D Visualizations', desc: 'Realistic renderings that help you see and experience your space before construction begins.' }
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-[#2C2C2C] overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src={portfolioImages.hero[1]}
            alt="Services Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#2C2C2C] via-[#2C2C2C]/90 to-[#2C2C2C]/70" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4 animate-fade-in">
              Our Services
            </span>
            <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-6 leading-tight animate-fade-in-up">
              Comprehensive Design
              <span className="block text-[#D4956A]">Solutions for Every Space</span>
            </h1>
            <p className="text-white/70 text-lg leading-relaxed animate-fade-in-up delay-200">
              From concept to completion, we offer a full spectrum of interior design 
              services tailored to your unique vision, lifestyle, and requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Service Categories Navigation */}
      <section className="py-8 bg-[#F5F0E8] sticky top-[72px] z-40 border-b border-[#E8E3DB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8">
            {serviceCategories.map((cat) => (
              <a
                key={cat.id}
                href={`#${cat.id}`}
                className="flex items-center text-sm text-[#6B6B6B] hover:text-[#B87333] transition-colors duration-300 group"
              >
                <cat.icon size={18} className="mr-2 group-hover:text-[#B87333] transition-colors" />
                {cat.title}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Service Sections */}
      {serviceCategories.map((category, categoryIndex) => (
        <section
          key={category.id}
          id={category.id}
          className={`py-24 lg:py-32 ${categoryIndex % 2 === 0 ? 'bg-[#FDFBF7]' : 'bg-[#F5F0E8]'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section Header */}
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-16">
              <div className={categoryIndex % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#B87333]/10 border border-[#B87333]/30 mr-4">
                    <category.icon size={24} className="text-[#B87333]" />
                  </div>
                  <span className="text-[#B87333] text-sm tracking-[0.2em] uppercase">
                    {category.subtitle}
                  </span>
                </div>
                <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
                  {category.title}
                </h2>
                <div className="divider-copper mb-6" />
                <p className="text-[#6B6B6B] text-lg leading-relaxed">
                  {category.description}
                </p>
              </div>
              <div className={`aspect-[4/3] overflow-hidden ${categoryIndex % 2 === 1 ? 'lg:order-1' : ''}`}>
                <img
                  src={category.image}
                  alt={category.title}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>

            {/* Service Items */}
            <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
              {category.items.map((item, index) => (
                <div
                  key={index}
                  className="p-8 bg-white border border-[#E8E3DB] hover:border-[#B87333]/30 transition-all duration-300 hover:-translate-y-1 group"
                >
                  <div className="flex items-start">
                    <div className="w-12 h-12 flex items-center justify-center bg-[#B87333]/5 border border-[#B87333]/20 mr-5 flex-shrink-0 group-hover:bg-[#B87333] group-hover:border-[#B87333] transition-all duration-300">
                      <item.icon size={22} className="text-[#B87333] group-hover:text-white transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-[#2C2C2C] mb-2 group-hover:text-[#B87333] transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-[#6B6B6B] text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Process Section */}
      <section className="py-24 lg:py-32 bg-[#2C2C2C]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
              Our Process
            </span>
            <h2 className="font-serif text-4xl lg:text-5xl text-white mb-6">
              How We Work
            </h2>
            <p className="text-[#9A9A9A] max-w-2xl mx-auto">
              Our streamlined process ensures a smooth journey from initial concept 
              to final execution, keeping you informed and involved every step of the way.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'We begin with an in-depth consultation to understand your vision, needs, and lifestyle.' },
              { step: '02', title: 'Design', desc: 'Our team creates detailed concepts, 3D visualizations, and material selections.' },
              { step: '03', title: 'Development', desc: 'We refine the design based on your feedback and finalize all specifications.' },
              { step: '04', title: 'Delivery', desc: 'Expert execution and installation, followed by a seamless handover.' }
            ].map((phase, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="font-serif text-6xl text-[#B87333]/20 mb-4">
                    {phase.step}
                  </div>
                  <h3 className="font-serif text-2xl text-white mb-3">
                    {phase.title}
                  </h3>
                  <p className="text-[#9A9A9A] text-sm leading-relaxed">
                    {phase.desc}
                  </p>
                </div>
                {index < 3 && (
                  <div className="hidden md:block absolute top-8 -right-4 w-8">
                    <ChevronRight size={24} className="text-[#B87333]/40" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 bg-[#F5F0E8]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <span className="inline-block text-[#B87333] text-sm tracking-[0.2em] uppercase mb-4">
            Ready to Begin?
          </span>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#2C2C2C] mb-6">
            Let's Create Something
            <span className="block text-[#B87333]">Extraordinary Together</span>
          </h2>
          <p className="text-[#6B6B6B] text-lg mb-10 max-w-2xl mx-auto">
            Whether you're planning a complete transformation or a simple refresh, 
            we're here to bring your vision to life with expertise and passion.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-10 py-5 bg-[#B87333] text-white text-sm tracking-wider uppercase hover:bg-[#8B5A2B] transition-all duration-300 hover:-translate-y-0.5 group"
            >
              Start Your Project
              <ArrowRight size={16} className="ml-3 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/gallery"
              className="inline-flex items-center justify-center px-10 py-5 border border-[#B87333] text-[#B87333] text-sm tracking-wider uppercase hover:bg-[#B87333] hover:text-white transition-all duration-300"
            >
              View Our Portfolio
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ServicesPage;
